/** @jsxImportSource @emotion/react */
import React from 'react';
import {
  Button, Card, Empty, Popconfirm, Space, Spin, Tag, Typography,
} from 'antd';
import { HistoryOutlined } from '@ant-design/icons';
import commonStyles from '../../../../styles/commonStyles';
import { styles } from '../styles';
import {
  buildAppliedHarvestBaseById,
  formatEventDateTime,
  getEventDisplayName,
  isAppliedQuantityAffectedEvent,
  isImmutableAppliedEvent,
  renderLocationPayloadSummary,
  resolveEventDate,
  shouldShowQuantityAffected,
} from '../utils';
import type { PropertyHistory } from '../hooks/useEventHistory';
import type { PlantingEvent } from '../../../../types/plantingEvents';

const { Title, Text } = Typography;

interface Props {
  historyLoaded: boolean;
  loadingEvents: boolean;
  fetchingEvents: boolean;
  events: PlantingEvent[];
  propertyHistory: PropertyHistory;
  plantingQuantity?: number | null;
  initialPlantingQuantity?: number | null;
  plantingLocations?: any[];
  onLoadHistory: () => void;
  onEditEvent: (event: PlantingEvent) => void;
  onDeleteEvent: (eventId: string) => void;
}

const EventHistoryCard: React.FC<Props> = ({
  historyLoaded,
  loadingEvents,
  fetchingEvents,
  events,
  propertyHistory,
  plantingQuantity,
  initialPlantingQuantity,
  plantingLocations,
  onLoadHistory,
  onEditEvent,
  onDeleteEvent,
}) => (
  <Card
    title="История событий"
    extra={
      <Button
        icon={<HistoryOutlined />}
        loading={loadingEvents || fetchingEvents}
        onClick={onLoadHistory}
        css={styles.historyBtn}
      >
        {historyLoaded ? 'Обновить' : 'Загрузить'}
      </Button>
    }
  >
    {!historyLoaded && !loadingEvents && (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Нажмите на кнопку загрузки для просмотра истории" />
    )}
    {(loadingEvents || fetchingEvents) && <Spin />}
    {historyLoaded && !loadingEvents && events.length === 0 && (
      <Text type="secondary">Событий пока нет</Text>
    )}
    {historyLoaded && !loadingEvents && events.length > 0 && (
      <Space direction="vertical" size={16} css={commonStyles.fullWidth}>
        <MeasurementsSection measurements={propertyHistory.measurements} />
        <LocationsSection locations={propertyHistory.locations} />
        <EventFeedSection
          events={propertyHistory.otherEvents}
          plantingQuantity={plantingQuantity}
          initialPlantingQuantity={initialPlantingQuantity}
          plantingLocations={plantingLocations}
          onEdit={onEditEvent}
          onDelete={onDeleteEvent}
        />
      </Space>
    )}
  </Card>
);

// ── Измерения ──
const MeasurementsSection: React.FC<{
  measurements: PropertyHistory['measurements'];
}> = ({ measurements }) => {
  const keys = Object.keys(measurements);
  if (keys.length === 0) return null;

  return (
    <div>
      <Title level={5}>Измерения</Title>
      {keys.map((propName) => {
        const records = measurements[propName];
        return (
          <div key={propName} css={styles.measurementBlock}>
            <Text strong css={styles.measurementLabel}>
              {propName}{' '}
              {records[0]?.unit && <Text type="secondary">({records[0].unit})</Text>}
            </Text>
            <div css={styles.measurementTimeline}>
              {records.map((record, idx) => (
                <div key={idx} css={styles.measurementRow}>
                  <Text type="secondary" css={styles.measurementDate}>
                    {formatEventDateTime(record.date)}
                  </Text>
                  <Tag color="blue">{record.value}</Tag>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Локации ──
const LocationsSection: React.FC<{
  locations: PropertyHistory['locations'];
}> = ({ locations }) => {
  if (locations.length === 0) return null;

  return (
    <div>
      <Title level={5}>Локация</Title>
      <div css={styles.measurementTimeline}>
        {locations.map((loc, idx) => (
          <div key={idx} css={styles.locationEntry}>
            <Text type="secondary" css={styles.locationDate}>
              {formatEventDateTime(loc.date)}
            </Text>
            <Text>{loc.location}</Text>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Лента событий ──
const EventFeedSection: React.FC<{
  events: PlantingEvent[];
  plantingQuantity?: number | null;
  initialPlantingQuantity?: number | null;
  plantingLocations?: any[];
  onEdit: (event: PlantingEvent) => void;
  onDelete: (eventId: string) => void;
}> = ({ events, plantingQuantity, initialPlantingQuantity, plantingLocations, onEdit, onDelete }) => {
  // Per-harvest base "X из Y" = the stand BEFORE that pull, not the current
  // remainder: harvesting 10 then 9 from 50 reads "10 из 50" then "9 из 40".
  // Walk applied whole-plant harvests oldest→newest, subtracting each pull from the
  // initial stand (preferred from the backend; else reconstructed as current +
  // everything confirmed so far). Proposed events remain visible but inert.
  const harvestBaseById = React.useMemo(() => {
    return buildAppliedHarvestBaseById(events, plantingQuantity, initialPlantingQuantity);
  }, [events, plantingQuantity, initialPlantingQuantity]);

  if (events.length === 0) return null;

  return (
    <div>
      <Title level={5}>Лента событий</Title>
      <Space direction="vertical" size={8} css={commonStyles.fullWidth}>
        {events.map((event) => {
          const isAppliedQuantityEvent = isAppliedQuantityAffectedEvent(event);
          const decisionState = String(event.decisionState || 'confirmed').toLowerCase();
          const showQuantity = shouldShowQuantityAffected(event, plantingQuantity);
          const quantityBase = harvestBaseById.get(event.id)
            ?? initialPlantingQuantity ?? plantingQuantity ?? event.plantingQuantity ?? '?';

          return (
            <Card key={event.id} size="small" css={styles.eventCard}>
              <Space direction="vertical" size={4} css={commonStyles.fullWidth}>
                <div css={styles.eventMeta}>
                  {resolveEventDate(event) && (
                    <Text css={styles.dateText}>{formatEventDateTime(resolveEventDate(event))}</Text>
                  )}
                  {!event.actualDate && event.plannedDate && (
                    <Text type="secondary">план: {formatEventDateTime(event.plannedDate)}</Text>
                  )}
                  <Text css={styles.eventName}>{getEventDisplayName(event)}</Text>
                  {event.status === 'pending' && <Tag color="orange">Ожидает</Tag>}
                  {event.status === 'planned' && <Tag color="default">Запланировано</Tag>}
                  {decisionState !== 'confirmed' && decisionState !== 'rejected' && <Tag color="purple">Не подтверждено</Tag>}
                  {decisionState === 'rejected' && <Tag color="red">Отклонено</Tag>}
                  {showQuantity && (
                    <Text css={styles.partialQty}>
                      {isAppliedQuantityEvent
                        ? `(${event.quantityAffected} из ${quantityBase})`
                        : `(${event.quantityAffected}, не применено)`}
                    </Text>
                  )}
                  <Space size={6} css={styles.eventActions}>
                    <Button size="small" onClick={() => onEdit(event)}>Скорректировать</Button>
                    {!isImmutableAppliedEvent(event) && (
                      <Popconfirm
                        title="Удалить событие?"
                        description="Запись исчезнет из истории. Действие необратимо."
                        okText="Удалить"
                        cancelText="Отмена"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => onDelete(event.id)}
                      >
                        <Button size="small" danger>Удалить</Button>
                      </Popconfirm>
                    )}
                  </Space>
                </div>
                {event.notes && <Text type="secondary">{event.notes}</Text>}
                {event.properties
                  ?.filter((p) => p.propertyCode === 'planting_location_payload')
                  .map((p) => {
                    const summary = renderLocationPayloadSummary(p.valueText, plantingLocations || []);
                    return summary ? (
                      <Text key={`${event.id}-${p.propertyId}`} type="secondary">
                        Локация: {summary}
                      </Text>
                    ) : null;
                  })}
              </Space>
            </Card>
          );
        })}
      </Space>
    </div>
  );
};

export default EventHistoryCard;
